import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { LocaleService } from '@core/services/locale.service';
import { AuthStore } from '@core/store/core/auth.store';
import { LucideGlobe, LucideLogOut, LucideMoon, LucideSettings, LucideSun } from '@lucide/angular';
import { MmscanButton } from '@mmscan/angular-ui/button';
import { MmscanThemeService } from '@mmscan/angular-ui/core';
import { MmscanConfirmService } from '@mmscan/angular-ui/dialog';
import { MmscanIcon } from '@mmscan/angular-ui/icon';
import { type MmscanMenuOption, MmscanPopupMenu } from '@mmscan/angular-ui/menu';
import { MmscanTooltip } from '@mmscan/angular-ui/tooltip';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

/** Settings menu (theme, language) and log out, in the shell's header. */
@Component({
  selector: 'mp-header-actions',
  imports: [MmscanPopupMenu, MmscanButton, MmscanIcon, MmscanTooltip, TranslatePipe],
  template: `
    <mmscan-popup-menu
      variant="ghost"
      severity="secondary"
      [icon]="settingsIcon"
      [items]="settings()"
      [ariaLabel]="'nav.settings.title' | translate"
    />
    <button
      mmscanButton
      type="button"
      variant="ghost"
      severity="secondary"
      size="icon"
      [attr.aria-label]="'nav.settings.logout' | translate"
      [mmscanTooltip]="'nav.settings.logout' | translate"
      (click)="logout()"
    >
      <mmscan-icon [icon]="logoutIcon" size="sm" />
    </button>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'flex items-center gap-1' },
})
export class HeaderActionsComponent {
  private readonly theme = inject(MmscanThemeService);
  private readonly authStore = inject(AuthStore);
  private readonly localeService = inject(LocaleService);
  private readonly translate = inject(TranslateService);
  private readonly confirm = inject(MmscanConfirmService);

  protected readonly settingsIcon = LucideSettings;
  protected readonly logoutIcon = LucideLogOut;

  protected readonly settings = computed<MmscanMenuOption[]>(() => {
    this.localeService.locale();
    const dark = this.theme.theme() === 'dark';
    return [
      {
        label: this.translate.instant(dark ? 'nav.settings.dark' : 'nav.settings.light'),
        icon: dark ? LucideMoon : LucideSun,
        command: () => this.theme.toggle(),
      },
      {
        // The active language; choosing it switches to the other one.
        label: this.translate.instant(
          this.authStore.userLocale() === 'EN_CA' ? 'nav.settings.languageEnglish' : 'nav.settings.languageFrench'
        ),
        icon: LucideGlobe,
        command: () => this.authStore.switchLocale(),
      },
    ];
  });

  protected async logout(): Promise<void> {
    const confirmed = await this.confirm.confirm({
      title: this.translate.instant('nav.settings.logoutConfirmHeader'),
      message: this.translate.instant('nav.settings.logoutConfirmMessage'),
      acceptLabel: this.translate.instant('nav.settings.logoutConfirmAccept'),
      rejectLabel: this.translate.instant('common.actions.cancel'),
    });
    if (confirmed) this.authStore.logout();
  }
}
